// components/DetachedStateClarificationModal.tsx
import React, { useCallback } from 'react';
import { DetachedStateClarificationPayload } from '#types/types';
import { History, CheckCircle } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';

const DetachedStateClarificationModal: React.FC = () => {
  const { kernel } = useKernel();
  const isOpen = useKernelStore((state) => state.activeModal === 'DetachedStateClarification');
  const payload = useKernelStore((state) => state.modalPayload as DetachedStateClarificationPayload | null);

  const handleGoToLatest = useCallback(() => {
    if (payload) kernel.current?.resolveDetachedState('latest', payload);
  }, [kernel, payload]);

  const handlePromote = useCallback(() => {
    if (payload) kernel.current?.resolveDetachedState('promote', payload);
  }, [kernel, payload]);

  const handleRootClick = useCallback(() => {
    kernel.current?.closeModal();
  }, [kernel]);

  const handleModalClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  if (!isOpen) return null;

  // ChatGPT's dialog (owner, 2026-09-28): one raised #212121 sheet, a plain question, the choices
  // as rows that light up on hover, the recommended one already lit, and a quiet round Cancel.
  const row = 'flex w-full items-start gap-3 rounded-[16px] px-4 py-3 text-left transition-colors';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={handleRootClick}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="detached-state-title"
        className="relative w-full max-w-[480px] rounded-[24px] border border-white/[0.06] bg-[#303030] p-6 text-white shadow-[0_24px_64px_rgba(0,0,0,0.6)]"
        onClick={handleModalClick}
      >
        <h2 id="detached-state-title" className="font-semibold text-[#ECECEC]" style={{ fontSize: 18, lineHeight: '26px' }}>
          Continue from an older version?
        </h2>
        <p className="mt-1.5 text-[#AFAFAF]" style={{ fontSize: 14, lineHeight: '21px' }}>
          You asked the assistant to work on an older version. How would you like to go on?
        </p>

        <div className="mt-5 flex flex-col gap-1.5">
          <button type="button" onClick={handleGoToLatest} className={row + ' bg-white/[0.06] hover:bg-white/[0.09]'}>
            <History size={20} className="mt-0.5 shrink-0 text-[#ECECEC]" />
            <span className="min-w-0">
              <span className="flex items-center gap-2 font-medium text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '20px' }}>
                Go to the latest and continue
                <span className="rounded-full bg-[#3862C2]/25 px-2 py-0.5 text-[#9DB9F5]" style={{ fontSize: 11, lineHeight: '14px' }}>Recommended</span>
              </span>
              <span className="mt-0.5 block text-[#AFAFAF]" style={{ fontSize: 13, lineHeight: '18px' }}>
                Leaves the old version and applies your request to the latest validated work.
              </span>
            </span>
          </button>

          <button type="button" onClick={handlePromote} className={row + ' hover:bg-white/[0.06]'}>
            <CheckCircle size={20} className="mt-0.5 shrink-0 text-[#ECECEC]" />
            <span className="min-w-0">
              <span className="block font-medium text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '20px' }}>
                Make this the main version
              </span>
              <span className="mt-0.5 block text-[#AFAFAF]" style={{ fontSize: 13, lineHeight: '18px' }}>
                Rolls the workspace back, so this older version becomes the validated main one.
              </span>
            </span>
          </button>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleRootClick}
            className="inline-flex h-9 items-center rounded-full border border-[#3A3A3A] px-4 font-medium text-[#ECECEC] transition-colors hover:bg-white/[0.06]"
            style={{ fontSize: 14 }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(DetachedStateClarificationModal);

